import React, { Dispatch, SetStateAction } from "react";

import { DataStructure } from "@/components/StaticTable";
import { Filter, FiltersPayloads } from "@/types/ApiResponse";
import { Card } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import moment from "moment/moment";
import CloseIcon from "@material-ui/icons/Close";
import { useTranslation } from "react-i18next";

import useStyles from "@/components/StaticTable/FiltersSection/style";
import { OPERATORS } from "@/components/StaticTable/Filters/operators";

interface FiltersSectionProps<T> {
  dataStructure: DataStructure<T>[];
  filters: FiltersPayloads;
  setFilters: Dispatch<SetStateAction<FiltersPayloads>>;
  setColonneSelectionneePourFiltre: React.Dispatch<
    React.SetStateAction<DataStructure<T> | null>
  >;
  setDefaultFilterPopinData: any;
}
export function FiltersSection<T>({
  dataStructure,
  filters,
  setFilters,
  setColonneSelectionneePourFiltre,
  setDefaultFilterPopinData,
}: FiltersSectionProps<T>) {
  const classes = useStyles();
  const { t } = useTranslation();

  const onDeleteFilter = (clickedFilter: Filter) => {
    if (clickedFilter.operateur === "BETWEEN") {
      setFilters(
        filters.filter(
          (filter) =>
            filter.valueMin !== clickedFilter.valueMin ||
            filter.valueMax !== clickedFilter.valueMax ||
            filter.key !== clickedFilter.key ||
            filter.operateur !== clickedFilter.operateur
        )
      );
    } else {
      setFilters(
        filters.filter(
          (filter) =>
            filter.value !== clickedFilter.value ||
            filter.key !== clickedFilter.key ||
            filter.operateur !== clickedFilter.operateur
        )
      );
    }
  };

  const getColumnLabel = (filtreStr: string) => {
    let listFiltres = dataStructure
      .map((ds) => ds.filter)
      .filter(Boolean)
      .flat();

    let filtre = listFiltres.find((filtre) => {
      return filtre?.key === filtreStr;
    });

    let ds = dataStructure.find((ds) => {
      if (!ds.filter) return false;
      if (Array.isArray(ds.filter)) {
        return ds.filter.some((f) => f.key === filtre?.key);
      }
      return ds.filter.key === filtre?.key;
    });

    return filtre ? ("label" in filtre ? filtre.label : ds?.label) : filtreStr;
  };

  const getOperateurSymbol = (operateurStr: string) => {
    let op = OPERATORS.find((operateur) => {
      return operateur.value === operateurStr;
    });

    return op ? t(op.label) : operateurStr;
  };

  const getValue = (filtreKey: string, value?: string | number | Date) => {
    let listFiltres = dataStructure
      .map((ds) => ds.filter)
      .filter(Boolean)
      .flat();

    let filtre = listFiltres.find((filtre) => {
      return filtre?.key === filtreKey;
    });

    return filtre && filtre.rowType === "date" && value
      ? moment(new Date(value)).format("DD/MM/YYYY")
      : value;
  };

  if (!filters || filters.length === 0) return null;

  const onCardClick = (filter: Filter) => {
    let ds = dataStructure.find((ds) => {
      if (!ds.filter) return false;
      if (Array.isArray(ds.filter)) {
        return ds.filter.some((f) => f.key === filter.key);
      }
      return ds.filter.key === filter.key;
    });

    if (ds) setColonneSelectionneePourFiltre(ds);

    let op = OPERATORS.find((operateur) => {
      return operateur.value === filter.operateur;
    });

    setDefaultFilterPopinData({
      defaultKey: filter.key,
      defaultOperateur: op,
      defaultValeur: filter.value,
      defaultValeurMin: filter.valueMin,
      defaultValeurMax: filter.valueMax,
    });
  };

  return (
    <div className={classes.container}>
      {filters?.map((filter, index) => (
        <Card key={index}>
          <Typography color={"secondary"} onClick={() => onCardClick(filter)}>
            {filter.operateur === "BETWEEN"
              ? `${getColumnLabel(filter.key)} ${getOperateurSymbol(
                  filter.operateur
                )} '${getValue(
                  filter.key,
                  filter.valueMin?.toString()
                )}' & '${getValue(filter.key, filter.valueMax?.toString())}'`
              : filter.operateur === "IS_NULL"
              ? `${getColumnLabel(filter.key)} ${getOperateurSymbol(
                  filter.operateur
                )}`
              : `${getColumnLabel(filter.key)} ${getOperateurSymbol(
                  filter.operateur
                )} '${getValue(filter.key, filter.value)}'`}
          </Typography>
          <CloseIcon onClick={() => onDeleteFilter(filter)} />
        </Card>
      ))}
    </div>
  );
}
